import React from 'react';
import { Plus } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Semester } from '../../types/grades';
import { SortableCourse } from './SortableCourse';
import { CourseInput } from './CourseInput';
import { useSemesterHandlers } from './hooks/useSemesterHandlers';

interface SemesterContentProps {
  semester: Semester;
  semesters: Semester[];
  setSemesters: (semesters: Semester[]) => void;
  selectedSemesterId: string;
}

export function SemesterContent({ 
  semester,
  semesters,
  setSemesters,
  selectedSemesterId
}: SemesterContentProps) {
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const { handleCourseChange, addCourse, deleteCourse } = useSemesterHandlers(
    semesters,
    setSemesters,
    selectedSemesterId
  );
  
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 200,
      tolerance: 5,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over?.id && over) {
      const oldIndex = parseInt(active.id.split('-')[1]);
      const newIndex = parseInt(over.id.split('-')[1]);
      
      setSemesters(prevSemesters => 
        prevSemesters.map(sem => {
          if (sem.id === selectedSemesterId) {
            return {
              ...sem,
              courses: arrayMove(sem.courses, oldIndex, newIndex)
            };
          }
          return sem;
        })
      );
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const getActiveCourse = () => {
    if (!activeId) return null;
    const index = parseInt(activeId.split('-')[1]);
    return semester.courses[index];
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">
          {semester.name} Courses
        </h2>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={semester.courses.map((_, index) => `course-${index}-${semester.id}`)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {semester.courses.map((course, index) => (
              <SortableCourse
                key={`course-${index}-${semester.id}`}
                id={`course-${index}-${semester.id}`}
                course={course}
                index={index}
                onChange={(updatedCourse) => handleCourseChange(index, updatedCourse)}
                onDelete={() => deleteCourse(index)}
              />
            ))}
          </div>
        </SortableContext>

        <DragOverlay adjustScale={true} dropAnimation={null}>
          {activeId && getActiveCourse() ? (
            <CourseInput
              course={getActiveCourse()!}
              onChange={() => {}}
              onDelete={() => {}}
              index={-1}
              isDragging={true}
              style={{
                width: '100%',
                height: 'auto',
                cursor: 'grabbing',
              }}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <button
        onClick={addCourse}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md 
                 hover:bg-blue-700 transition-colors duration-200 ease-in-out
                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        <Plus size={20} />
        Add Course
      </button>
    </div>
  );
}